// ============================================
// Quiz Service
// Swap mock data for real API calls when ready
// ============================================
import { getQuizById } from '../data/quizzes';
import {apiRequest} from "./api";

const BASE_URL = process.env.REACT_APP_API_URL || '';
const USE_MOCK = !BASE_URL;

const delay = (ms = 400) => new Promise((res) => setTimeout(res, ms));

/**
 * Start a quiz.
 * @param {number} tmdbId
 * @param {'movie'|'tv'} kind
 * @param {number|null} seasonNumber  — required when kind is 'tv'
 */
// A quiz start may have to generate questions with an AI provider, which can
// take longer than the default 15s request timeout. The backend bounds the
// whole run (QUIZ_GENERATION_TIMEOUT_MS) and returns quiz_generation_timeout,
// so the client waits a little longer than that and NEVER retries: a replayed
// POST would race the first request and could create a second attempt.
export const QUIZ_START_TIMEOUT_MS = 75_000;

// `requestId` correlates one start's analytics rows; `resumed` marks a start
// resumed after email verification.
export async function startQuiz(tmdbId, kind, seasonNumber = null, { sourceScreen = null, requestId = null, resumed = false } = {}) {
    const body = { tmdbId, kind };
    if (kind === 'tv' && seasonNumber !== null) body.seasonNumber = seasonNumber;
    if (sourceScreen) body.sourceScreen = sourceScreen;
    if (requestId) body.requestId = requestId;
    if (resumed) body.resumed = true;

    return apiRequest('/quiz/start', {
        method: 'POST',
        body,
        retry: { retries: 0, timeoutMs: QUIZ_START_TIMEOUT_MS },
    });
}

/**
 * Answer-free availability for one quiz target (GET /quiz/availability):
 * { quizReady, reason, availableAt, availableAtFormatted, earlyAccess, spoilerNotice }.
 */
export async function getQuizAvailability(tmdbId, kind, seasonNumber = null) {
    const params = { tmdbId, kind };
    if (kind === 'tv' && seasonNumber !== null) params.seasonNumber = seasonNumber;
    return apiRequest('/quiz/availability', { params });
}

/**
 * Fetch quiz by ID
 * Real endpoint: GET /quiz/:id
 */
export async function fetchQuiz(id) {
    if (USE_MOCK) {
        await delay();
        const quiz = getQuizById(id);
        if (!quiz) throw new Error('Challenge not found');
        return quiz;
    }
    const res = await fetch(`${BASE_URL}/quiz/${id}`);
    if (!res.ok) throw new Error("Couldn't load your challenge.");
    return res.json();
}

/**
 * Submit quiz answers
 * Real endpoint: POST /quiz/:id/submit
 * @param {string} id - quiz ID
 * @param {number[]} answers - array of selected option indices
 */
export async function submitQuiz(id, answers) {
    if (USE_MOCK) {
        await delay(600);
        const quiz = getQuizById(id);
        let correct = 0;
        quiz.questions.forEach((q, i) => {
            if (answers[i] === q.correctIndex) correct++;
        });
        const passed = correct >= quiz.passingScore;
        return { correct, total: quiz.totalQuestions, passed, coinName: quiz.coinName, coinTier: quiz.coinTier };
    }
    const res = await fetch(`${BASE_URL}/quiz/${id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
    });
    if (!res.ok) throw new Error("Couldn't finish your challenge. Please try again.");
    return res.json();
}